/**
 * src/lib/i18n/useLocalizedRows.js — TICKET-959.
 *
 * Give a screen its rows in the content language the viewer picked. 'orig' = the rows
 * as written. 'en'/'es' = each translatable field swapped for its fresh stored copy
 * (stale/missing -> the source text, never blank). Rows lacking a fresh copy are
 * translated ONCE per (table, lang, row set) through translations.translate, then the
 * caller's `reload` refreshes them (cached server-side, so the next toggle is instant).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { localizeRow, needsTranslation, TRANSLATABLE_FIELDS, withKnownSource } from '../../../lib/quote-doc/translations.mjs';
import { translateContent } from '../api/translations';

/**
 * @param {string} table     a TRANSLATABLE_FIELDS table
 * @param {object[]} rows    rows including `translations`
 * @param {'orig'|'en'|'es'} lang
 * @param {{ reload?: () => any, fallbackLang?: 'en'|'es' }} [opts]
 * @returns {{ rows: object[], translating: boolean, error: string|null }}
 */
export function useLocalizedRows(table, rows, lang, { reload, fallbackLang = 'es' } = {}) {
  const fields = TRANSLATABLE_FIELDS[table];
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState(null);
  const asked = useRef(new Set());
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  const pending = useMemo(() => {
    if (!fields || lang === 'orig') return [];
    // TICKET-1464: AI-written rows (photo summaries) carry a known source, never a detected one.
    return (rows || []).filter((r) => r?.id && needsTranslation(withKnownSource(r, table), fields, lang, fallbackLang)).map((r) => r.id);
  }, [rows, lang, table, fields, fallbackLang]);
  const pendingKey = pending.join(',');

  useEffect(() => {
    if (!pending.length) return undefined;
    const key = `${table}|${lang}|${pendingKey}`;
    if (asked.current.has(key)) return undefined; // one call per row set: never a loop
    asked.current.add(key);
    let alive = true;
    setTranslating(true);
    setError(null);
    translateContent({ table, ids: pending.slice(0, 200), lang, fallbackLang })
      .then((r) => {
        if (!alive) return;
        if (r?.killed) setError('paused');
        else if (r?.success === false) setError(r?.error || 'failed');
        else reloadRef.current?.();
      })
      .catch((e) => { if (alive) setError(e?.message || 'failed'); })
      .finally(() => { if (alive) setTranslating(false); });
    return () => { alive = false; };
    // pendingKey captures `pending`; table/lang/fallbackLang are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, lang, pendingKey, fallbackLang]);

  const out = useMemo(() => {
    if (!fields || lang === 'orig') return rows || [];
    return (rows || []).map((r) => {
      const out = localizeRow(withKnownSource(r, table), table, lang, fallbackLang);
      return out && r ? { ...out, translations: r.translations } : out;
    });
  }, [rows, lang, table, fields, fallbackLang]);

  return { rows: out, translating, error };
}
